import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { setSessionGraceActive } from "~/auth/sessionGrace";

/**
 * When the session expires we keep the user on their current page (see
 * AuthSessionProvider's grace path + SessionExpiredBanner). This hook enforces
 * the redirect to /login only once they try to navigate to a *different* route,
 * so an expired session doesn't yank them out of what they were doing.
 *
 * Mount once inside the authenticated layout (it needs router context).
 */
export function useSessionExpiryRedirect(): void {
  const { sessionExpired } = useAuthSession();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // Pathname where the session expired. null = not currently expired.
  const expiredAtPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!sessionExpired) {
      expiredAtPathRef.current = null;
      return;
    }

    // First render after expiry: remember where they were and let them stay.
    if (expiredAtPathRef.current === null) {
      expiredAtPathRef.current = pathname;
      return;
    }

    // They navigated to a new route while expired — now send them to sign in,
    // preserving where they were heading as the returnUrl.
    if (pathname !== expiredAtPathRef.current) {
      setSessionGraceActive(false);
      const returnUrl = encodeURIComponent(pathname + search);
      navigate(`/login?returnUrl=${returnUrl}`, { replace: true });
    }
  }, [sessionExpired, pathname, search, navigate]);
}
